import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import LoadingSpinner from './LoadingSpinner';

const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    const { currentUser, userData, userRole, companyData, loading } = useAuth();

    if (loading) return <LoadingSpinner />;
    if (!currentUser) return <Navigate to="/login" replace />;
    if (!userData) return <Navigate to="/register" replace />;

    const role = String(userRole || userData?.role || '').toLowerCase();
    const platformAdmin = ['super_admin', 'superadmin', 'platform_admin'].includes(role);

    // Registration drafts are authenticated but not yet workspace-ready.
    // Always return them to their resumable registration flow.
    const draftRegistration = userData?.profileCompleted === false
        || userData?.registrationIncomplete === true
        || ['individual_draft', 'join_company_draft', 'create_company_draft'].includes(String(userData?.registrationMode || '').toLowerCase());
    if (!platformAdmin && draftRegistration) return <Navigate to="/register" replace />;

    // Authentication alone must never bypass company subscription/seat state.
    // New-company owners remain on registration until the company subscription
    // payment activates the workspace. Existing-company users remain blocked
    // until the company administrator approves their reserved seat.
    if (!platformAdmin && userData?.isActive === false) {
        if (userData?.registrationMode === 'create_company') return <Navigate to="/register" replace />;
        return <Navigate to="/login" replace />;
    }
    if (!platformAdmin && !['admin', 'company_admin'].includes(role) && userData?.isApproved === false) {
        return <Navigate to="/login" replace />;
    }

    if (!platformAdmin && companyData?.subscription) {
        const subscription = companyData.subscription || {};
        const status = String(subscription.status || companyData.subscriptionStatus || '').toLowerCase();
        const expiryValue = subscription.expiryDate || subscription.expiresAt;
        const expiry = expiryValue?.toDate ? expiryValue.toDate() : expiryValue?.seconds ? new Date(expiryValue.seconds * 1000) : expiryValue ? new Date(expiryValue) : null;
        const expired = expiry instanceof Date && !Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now();
        if (expired || ['expired', 'cancelled', 'canceled', 'suspended', 'pending_payment', 'inactive'].includes(status)) {
            const canOpenRenewal = location.pathname === '/dashboard/company/settings' && (['admin', 'company_admin'].includes(role) || userData?.isOwner === true);
            if (!canOpenRenewal) return <Navigate to="/dashboard/company/settings?tab=subscription" replace />;
        }
    }

    return children;
};

export default ProtectedRoute;
